import * as THREE from 'three';

import { EffectComposer, RenderPass } from 'postprocessing';
import { makeEDLShadeMaterial } from './edlShadeMaterial';
import { EDLShadePass } from './edlShadePass';
import { makeBilateraFilterMaterial } from './BilateralFilterMaterial';
import { BilateralFilterPass } from './BilateralFilterPass';
import { ViewerLayer } from '../ViewerLayer';
import { PointCloudMaterial } from '../PointCloudMaterial';

type EDLPassParameters = {
  pixScale: number;
  isFilterEnabled: boolean;
  halfSize: number;
  sigma: number;
  sigmaZ: number;
};

type EDLComposerEntry = {
  target: THREE.WebGLRenderTarget;
  edlShadeMaterial: THREE.ShaderMaterial;
  bilateralFilterMaterial: THREE.ShaderMaterial | undefined;
  composer: EffectComposer;
};

export class EDLShadeRenderer {
  public targets: THREE.WebGLRenderTarget[];

  public renderer: THREE.WebGLRenderer;

  public scene: THREE.Scene;

  public camera: THREE.Camera;

  private screenSize: THREE.Vector2;

  private passes: EDLPassParameters[];

  private composerEntries: EDLComposerEntry[];

  private lightDirection: THREE.Vector3;

  private expScale: number;

  constructor(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera) {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;
    this.expScale = 100;
    this.screenSize = new THREE.Vector2();

    this.renderer.getSize(this.screenSize);

    this.passes = [
      {
        pixScale: 1,
        // bilateralFilter shader is broken (disabled)
        isFilterEnabled: false,
        halfSize: 1,
        sigma: 1,
        sigmaZ: 0.2,
      },
      {
        pixScale: 2,
        isFilterEnabled: false,
        halfSize: 2,
        sigma: 2,
        sigmaZ: 0.4,
      },
      {
        pixScale: 4,
        isFilterEnabled: false,
        halfSize: 2,
        sigma: 2,
        sigmaZ: 0.4,
      },
    ];

    const targetSizes = [1, 0.5, 0.25].map((scale) => {
      const { x, y } = this.screenSize;
      return { x: Math.round(x * scale), y: Math.round(y * scale) };
    });

    this.targets = [...Array(3).keys()].map((_, index) => {
      const { x, y } = targetSizes[index];
      return EDLShadeRenderer.makeTarget(x, y);
    });

    const ldPhi = Math.PI / 2;
    const ldTheta = Math.PI / 2;

    this.lightDirection = new THREE.Vector3(
      Math.sin(ldPhi) * Math.cos(ldTheta),
      Math.cos(ldPhi),
      Math.sin(ldPhi) * Math.sin(ldTheta)
    );

    const isPerspective = this.camera instanceof THREE.PerspectiveCamera;

    const zoom = isPerspective ? 1 : (this.camera as THREE.OrthographicCamera).zoom;
    const distanceToNeighborPixel = zoom * (isPerspective ? 3 : 1.2);

    const zNear = isPerspective ? (this.camera as THREE.PerspectiveCamera).near : 0;
    const zFar = isPerspective ? (this.camera as THREE.PerspectiveCamera).far : 1;

    this.composerEntries = [2, 1, 0].map((index) => {
      const { pixScale, isFilterEnabled, halfSize, sigma, sigmaZ } = this.passes[index];

      const edlShadeMaterial = makeEDLShadeMaterial({
        pixScale,
        expScale: this.expScale,
        zNear,
        zFar,
        screenSize: this.screenSize,
        distanceToNeighborPixel,
        isPerspective,
        lightDirection: this.lightDirection,
      });

      const targetSize = targetSizes[index];

      // biome-ignore lint/suspicious/noImplicitAnyLet: <explanation>
      let bilateralFilterMaterial;
      if (isFilterEnabled) {
        bilateralFilterMaterial = makeBilateraFilterMaterial({
          // halfSpatialSize: 1,
          // depthSigma: 0.2,
          // spatialSigma: 1,
          halfSpatialSize: halfSize,
          depthSigma: sigmaZ,
          spatialSigma: sigma,
          screenSize: new THREE.Vector2(targetSize.x, targetSize.y),
          isPerspective,
          zNear,
          zFar,
        });
      }

      // Using three resolutions {0,1,2}
      // {0,1,2} Render --> (RGBA + Depth) --> EDLShade --> (RGBA + Depth)
      // Mix {0,1,2} of (RGBA + Depth) --> (RGBA)

      const composer = new EffectComposer(renderer, {
        depthBuffer: true,
        stencilBuffer: false,
        frameBufferType: THREE.HalfFloatType, // ?
        // frameBufferType: THREE.FloatType,
        // multisampling: 0,
      });
      composer.autoRenderToScreen = false;
      composer.setSize(targetSize.x, targetSize.y);
      composer.addPass(new RenderPass(this.scene, this.camera));

      const target = this.targets[index];

      if (bilateralFilterMaterial) {
        composer.addPass(new EDLShadePass(edlShadeMaterial, undefined));
        composer.addPass(new BilateralFilterPass(bilateralFilterMaterial, target));
      } else {
        composer.addPass(new EDLShadePass(edlShadeMaterial, target));
      }
      return { target, edlShadeMaterial, bilateralFilterMaterial, composer };
    });
  }

  public setEdlStrength(edlStrength: number) {
    this.expScale = Math.max(edlStrength * 100, 1);
    this.composerEntries.forEach(({ edlShadeMaterial }) => {
      edlShadeMaterial.uniforms.Exp_scale.value = this.expScale;
      edlShadeMaterial.uniformsNeedUpdate = true;
    });
  }

  private static makeTarget(width: number, height: number): THREE.WebGLRenderTarget {
    const target = new THREE.WebGLRenderTarget(width, height, {
      // wrapS?: Wrapping | undefined;
      // wrapT?: Wrapping | undefined;
      // minFilter: THREE.NearestFilter,
      // magFilter: THREE.NearestFilter,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat, // RGBAFormat;
      // type: THREE.FloatType,
      // type: THREE.HalfFloatType,
      // anisotropy?: number | undefined; // 1;
      depthBuffer: false,
      stencilBuffer: false,
      generateMipmaps: false,
      colorSpace: THREE.LinearSRGBColorSpace,
      // colorSpace: THREE.SRGBColorSpace,
    });
    return target;
  }

  private renderTarget(
    index: number,
    timeDelta: number,
    pointCloudMaterialMap: Map<string, PointCloudMaterial>
  ) {
    const entry = this.composerEntries[index];
    const { pixScale } = this.passes[index];
    pointCloudMaterialMap.forEach(({ shaderMaterial }: PointCloudMaterial) => {
      shaderMaterial.uniforms.screenScale.value = 1 / pixScale;
      shaderMaterial.needsUpdate = true;
    });
    entry.composer.render(timeDelta);
  }

  public render(timeDelta: number, pointCloudMaterialMap: Map<string, PointCloudMaterial>) {
    const oldTarget = this.renderer.getRenderTarget();
    const oldMask = this.camera.layers.mask;

    this.camera.layers.disableAll();
    this.camera.layers.enable(ViewerLayer.EDLShaderLayer);
    // this.camera.layers.enable(ViewerLayer.Default);

    this.renderTarget(2, timeDelta, pointCloudMaterialMap);
    this.renderTarget(1, timeDelta, pointCloudMaterialMap);
    this.renderTarget(0, timeDelta, pointCloudMaterialMap);

    this.camera.layers.mask = oldMask;
    this.renderer.setRenderTarget(oldTarget);
  }

  public dispose() {
    this.targets.forEach((target) => {
      target.dispose();
    });
    this.composerEntries.forEach((entry) => {
      entry.bilateralFilterMaterial?.dispose();
      entry.edlShadeMaterial.dispose();
      entry.composer.dispose();
    });
  }
}

export default EDLShadeRenderer;
